import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cards } from '../assets/cards/index.ts';

import { HTV2AddProfilePictureCTA } from './components/HTV2ActionCardLinkCTA.tsx';
import {
	HTV2CreateWhiteboardCTA,
	HTV2CreateIntroPageCTA,
} from './components/HTV2CreateContentCTA.tsx';
import { HTV2ViewPopularContentCTA } from './components/HTV2ViewPopularContentCTA.tsx';
import { commonCardI18n } from './HTV2CommonCardsI18n.tsx';
import type { OnboardingActionStates } from './HTV2CardTypes.ts';

const i18n = defineMessages({
	addProfilePictureDescription: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture-nth-user.description',
		defaultMessage: 'Help teammates recognize you by adding a profile photo.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createIntroBlogTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-intro-blog.title',
		defaultMessage: 'Introduce yourself to your new peers',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createIntroBlogDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-intro-blog.description',
		defaultMessage:
			'Easily share your stories and interests with teammates by personalizing the intro template.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createIntroBlogCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-intro-blog.cta',
		defaultMessage: 'See templates',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a whiteboard and land them in the whiteboard editor',
	},
	createWhiteboardDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard-nth-user.description',
		defaultMessage:
			'Dream up new ideas by collaborating and visualizing in a freefrom way using whiteboards.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	viewPopularContentTitle: {
		id: 'experiment-home-onboarding-takeover-v2.view-popular-content.title',
		defaultMessage: 'Discover & react to trending content',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	viewPopularContentDescription: {
		id: 'experiment-home-onboarding-takeover-v2.view-popular-content.description',
		defaultMessage:
			'Dive into popular content and share your thoughts with a fun reaction or comment.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	viewPopularContentCTA: {
		id: 'experiment-home-onboarding-takeover-v2.view-popular-content.cta',
		defaultMessage: 'View popular content',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a whiteboard and land them in the whiteboard editor',
	},
});

export const NTH_USER_ONBOARDING_ACTIONS_DATA: readonly [
	{
		readonly id: 'viewPopularContent';
		readonly title: React.JSX.Element;
		readonly description: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		readonly image: {
			readonly light: any;
			readonly dark: any;
		};
		readonly cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
		readonly fadeCardOnComplete: true;
		readonly keepCtaOnCompleted: true;
	},
	{
		readonly id: 'createIntroBlog';
		readonly title: React.JSX.Element;
		readonly description: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		readonly image: {
			readonly light: any;
			readonly dark: any;
		};
		readonly cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
		readonly fadeCardOnComplete: true;
		readonly keepCtaOnCompleted: true;
	},
	{
		readonly id: 'createWhiteboard';
		readonly title: React.JSX.Element;
		readonly description: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		readonly image: {
			readonly light: any;
			readonly dark: any;
		};
		readonly cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
		readonly fadeCardOnComplete: true;
		readonly keepCtaOnCompleted: true;
	},
	{
		readonly id: 'addProfilePicture';
		readonly title: React.JSX.Element;
		readonly description: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		readonly image: {
			readonly light: any;
			readonly dark: any;
		};
		readonly cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
		readonly fadeCardOnComplete: true;
		readonly keepCtaOnCompleted: true;
	},
] = [
	{
		id: 'viewPopularContent',
		title: <FormattedMessage {...i18n.viewPopularContentTitle} />,
		description: i18n.viewPopularContentDescription,
		image: cards.viewPopularContent,
		cta: ({ onClick }: { onClick: () => void }): React.JSX.Element => (
			<HTV2ViewPopularContentCTA onClick={onClick}>
				<FormattedMessage {...i18n.viewPopularContentCTA} />
			</HTV2ViewPopularContentCTA>
		),
		fadeCardOnComplete: true,
		keepCtaOnCompleted: true,
	},
	{
		id: 'createIntroBlog',
		title: <FormattedMessage {...i18n.createIntroBlogTitle} />,
		description: i18n.createIntroBlogDescription,
		image: cards.createPage,
		cta: ({ onClick }: { onClick: () => void }): React.JSX.Element => (
			<HTV2CreateIntroPageCTA onClick={onClick}>
				<FormattedMessage {...i18n.createIntroBlogCTA} />
			</HTV2CreateIntroPageCTA>
		),
		fadeCardOnComplete: true,
		keepCtaOnCompleted: true,
	},
	{
		id: 'createWhiteboard',
		title: <FormattedMessage {...commonCardI18n.createWhiteboardTitle} />,
		description: i18n.createWhiteboardDescription,
		image: cards.createWhiteboard,
		cta: ({ onClick }: { onClick: () => void }): React.JSX.Element => (
			<HTV2CreateWhiteboardCTA onClick={onClick}>
				<FormattedMessage {...commonCardI18n.createWhiteboardCTA} />
			</HTV2CreateWhiteboardCTA>
		),
		fadeCardOnComplete: true,
		keepCtaOnCompleted: true,
	},
	{
		id: 'addProfilePicture',
		title: <FormattedMessage {...commonCardI18n.addProfilePictureTitle} />,
		description: i18n.addProfilePictureDescription,
		image: cards.addProfilePicture,
		cta: ({ onClick }: { onClick: () => void }): React.JSX.Element => (
			<HTV2AddProfilePictureCTA onClick={onClick}>
				<FormattedMessage {...commonCardI18n.addProfilePictureCTA} />
			</HTV2AddProfilePictureCTA>
		),
		fadeCardOnComplete: true,
		keepCtaOnCompleted: true,
	},
] as const;

export const transformNthUserOnboardingActions = ({
	onboardingActionStates,
}: {
	onboardingActionStates: OnboardingActionStates;
}): (
	| {
			state: 'pending' | 'dismissed' | 'completed';
			id: 'viewPopularContent';
			title: React.JSX.Element;
			description: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			image: {
				readonly light: any;
				readonly dark: any;
			};
			cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
			fadeCardOnComplete: true;
			keepCtaOnCompleted: true;
	  }
	| {
			state: 'pending' | 'dismissed' | 'completed';
			id: 'createIntroBlog';
			title: React.JSX.Element;
			description: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			image: {
				readonly light: any;
				readonly dark: any;
			};
			cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
			fadeCardOnComplete: true;
			keepCtaOnCompleted: true;
	  }
	| {
			state: 'pending' | 'dismissed' | 'completed';
			id: 'createWhiteboard';
			title: React.JSX.Element;
			description: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			image: {
				readonly light: any;
				readonly dark: any;
			};
			cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
			fadeCardOnComplete: true;
			keepCtaOnCompleted: true;
	  }
	| {
			state: 'pending' | 'dismissed' | 'completed';
			id: 'addProfilePicture';
			title: React.JSX.Element;
			description: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			image: {
				readonly light: any;
				readonly dark: any;
			};
			cta: ({ onClick }: { onClick: () => void }) => React.JSX.Element;
			fadeCardOnComplete: true;
			keepCtaOnCompleted: true;
	  }
)[] => {
	return NTH_USER_ONBOARDING_ACTIONS_DATA.map((action) => ({
		...action,
		state: onboardingActionStates[action.id] || 'pending',
	}));
};
